import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { LayoutDashboard, Target, Calendar, Home, Users, MessageCircle, ChevronRight, ChevronLeft, LogOut } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';

interface UnifiedNavigationProps {
  isCollapsed: boolean;
  onCollapse: () => void;
}

const navItems = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'توازنك' },
  { path: '/dashboard/goals', icon: Target, label: 'اهدافك' },
  { path: '/community', icon: Home, label: 'المجتمع' },
  { path: '/community/friends', icon: Users, label: 'الأصدقاء' },
  { path: '/community/messages', icon: MessageCircle, label: 'الدردشات' },
];

export const UnifiedNavigation: React.FC<UnifiedNavigationProps> = ({ isCollapsed, onCollapse }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuthStore();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActivePath = (path: string) => {
    if (path === '/dashboard' || path === '/community') {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="h-full flex flex-col">
      {/* Logo Header */}
      <div className="h-24 border-b flex flex-col items-center justify-center px-4">
        <AnimatePresence mode="wait">
          {!isCollapsed ? (
            <motion.div key="expanded" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex items-center justify-between w-full">
              <div className="flex items-center">
                <img src="/src/pics/logo.png" alt="توازن" className="w-8 h-8 rounded-lg" />
                <span className="mr-2 font-bold text-primary text-lg">توازن</span>
              </div>
              <button onClick={onCollapse} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
                <ChevronRight className="w-5 h-5" />
              </button>
            </motion.div>
          ) : (
            <motion.div key="collapsed" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center pt-4">
              <div className="flex flex-col items-center">
                <img src="/src/pics/logo.png" alt="توازن" className="w-8 h-8 rounded-lg mb-1" />
                <span className="text-xs font-bold text-primary">توازن</span>
              </div>
              <button onClick={onCollapse} className="p-2 rounded-lg hover:bg-gray-100 transition-colors mt-2">
                <ChevronLeft className="w-5 h-5" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Navigation Items */}
      <nav className="flex-1 p-4 overflow-y-auto" data-guide="navigation">
        <div className="space-y-2">
          {navItems.map(({ path, icon: Icon, label }) => (
            <Link
              key={path}
              to={path}
              className={`
                flex items-center space-x-3 rtl:space-x-reverse
                px-4 py-3 rounded-lg transition-all duration-200
                ${isActivePath(path) ? 'bg-primary bg-opacity-10 text-primary' : 'text-gray-600 hover:bg-gray-50'}
              `}
            >
              <Icon className="w-5 h-5 flex-shrink-0" />
              <AnimatePresence mode="wait">
                {!isCollapsed && (
                  <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex-1">
                    {label}
                  </motion.span>
                )}
              </AnimatePresence>
            </Link>
          ))}

          {/* Profile Link */}
          <Link
            to="/profile"
            className={`
              flex items-center space-x-3 rtl:space-x-reverse
              px-4 py-3 rounded-lg transition-all duration-200
              ${isActivePath('/profile') ? 'bg-primary bg-opacity-10 text-primary' : 'text-gray-600 hover:bg-gray-50'}
            `}
          >
            <div className="w-5 h-5 rounded-full bg-primary bg-opacity-10 flex items-center justify-center overflow-hidden flex-shrink-0">
              {user?.avatar ? (
                <img src={user.avatar} alt={user.name} className="w-full h-full object-cover" />
              ) : (
                <span className="text-primary font-medium text-xs">{user?.name?.charAt(0)?.toUpperCase()}</span>
              )}
            </div>
            <AnimatePresence mode="wait">
              {!isCollapsed && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex-1">
                  <p className="font-medium">{user?.name}</p>
                </motion.div>
              )}
            </AnimatePresence>
          </Link>
        </div>
      </nav>

      {/* Logout Button */}
      <div className="p-4 border-t">
        <button onClick={handleLogout} className="w-full flex items-center space-x-3 rtl:space-x-reverse px-4 py-3 rounded-lg text-error hover:bg-gray-50 transition-colors duration-200">
          <LogOut className="w-5 h-5" />
          {!isCollapsed && <span>تسجيل الخروج</span>}
        </button>
      </div>
    </div>
  );
};
